import React, { useMemo } from 'react'
import { Divider, Grid } from '@fs/zion-ui'
import HouseholdDisplay from './components/HouseholdDisplay'
import FloatingPersonsList from './components/FloatingPersonsList'
import LoadingList from './components/LoadingList'
import FilterAndActions from './components/FilterAndActions'
import CreateHouseholdCard from './components/CreateHouseholdCard'
import useGroupSectionsRework from './hooks/useGroupSectionsRework'

/**
 * @typedef {import('./hooks/useHouseholdsList').HouseholdListProps} PersonsListProps
 */

/**
 * @typedef {Object} Props
 * @property {(person: any) => void} onPersonSelect
 * @property {(id: string) => void} setSelectedRecordId
 * @property {string} [description]
 * @property {string} filterValue
 * @property {(value: string) => void} setFilterValue
 * @property {boolean} [showManageIndexesButton]
 */

/**
 * Renders a list of persons, grouped by household, and floating persons.
 *
 * NOTE: This component is designed to be used in conjunction with the `useHouseholdsList` hook
 * and a filter component (e.g., `FilterInput`) to manage state and filtering logic.
 *
 * @param {PersonsListProps & Props} props
 * @returns {JSX.Element}
 */
const HouseholdList = ({
  filteredHouseholdsWithPersons = [],
  filteredFloatingPersons = [],
  onPersonSelect,
  showLoading = false,
  isJiapu = false,
  setSelectedRecordId,
  description,
  filterValue,
  setFilterValue,
  showManageIndexesButton = true,
}) => {
  const groupSectionsRework = useGroupSectionsRework()

  // Filter out households with no persons to avoid rendering empty lists
  const cleanedHouseholdsWithPersons = useMemo(
    () => filteredHouseholdsWithPersons.filter(({ persons }) => persons && persons.length > 0),
    [filteredHouseholdsWithPersons]
  )

  // Early return loading skeletons if data is still loading
  if (showLoading) return <LoadingList />

  return (
    <>
      {/* Description and Filter */}
      <FilterAndActions
        description={description}
        filterValue={filterValue}
        onFilterChange={setFilterValue}
        showManageIndexesButton={showManageIndexesButton}
      />

      {/* Person List */}
      {!isJiapu && (
        <Grid guttersY={groupSectionsRework ? 'none' : 'xs'}>
          {/* List of Households with Persons */}
          {cleanedHouseholdsWithPersons.map(({ household, persons }) => (
            <HouseholdDisplay
              key={household?.id}
              household={household}
              persons={persons}
              setSelectedRecordId={setSelectedRecordId}
              onPersonSelect={onPersonSelect}
            />
          ))}

          {groupSectionsRework && cleanedHouseholdsWithPersons.length > 0 && <Divider marginY="xxs" fullWidth />}

          {/* Floating Persons (not attached to households) */}
          <FloatingPersonsList persons={filteredFloatingPersons} onPersonSelect={onPersonSelect} />
        </Grid>
      )}

      {/* Create Household Card at the end of the list */}
      <CreateHouseholdCard setSelectedRecordId={setSelectedRecordId} />
    </>
  )
}

export default HouseholdList
